import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { WorkspaceRoutes } from 'types/common';
import {
  HUD_1,
  NOTIFICATIONS,
  ROUTE_DISBURSEMENTS_AND_RECEIPTS,
} from 'utils/constants';
import { CurrentUser } from 'contexts/userContext';
import { useCheckUserPermissions } from 'hooks/useChekUserPermissions';

type CloseSidemenuProps = {
  t: TFunction;
  setSidemenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleViewChange: (view: WorkspaceRoutes, param?: string) => void;
  orderType: string;
  currentUser: CurrentUser | null;
};

type Section = 'general' | 'payments' | 'closing';

const useStyles = makeStyles((theme: any) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.2),
    paddingTop: theme.spacing(1.2),
    paddingBottom: theme.spacing(1.2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.main,
  },
  headerIcon: {
    color: theme.palette.primary.main,
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  iconButton: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.2),
    color: theme.palette.common.white,
    fontSize: theme.spacing(2.5),
    '&:hover': {
      backgroundColor: theme.accent.main,
      color: theme.palette.primary.main,
    },
  },
}));

const CloseSidemenu = ({
  t,
  setSidemenuOpen,
  handleViewChange,
  orderType,
  currentUser,
}: CloseSidemenuProps) => {
  const classes = useStyles();
  const { hasPermission } = useCheckUserPermissions(currentUser);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [section, setSection] = useState<Section | null>(null);

  const handleSectionOpen = (nextSection: Section, event: React.MouseEvent<HTMLButtonElement>) => {
    setSection(nextSection);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSection(null);
  };

  const handleRoute = (view: WorkspaceRoutes, param?: string) => {
    handleMenuClose();
    handleViewChange(view, param);
  };

  const showPaymentsMenu = hasPermission('createOrderLedgerEntry') || hasPermission('createTransferRequest');

  return (
    <>
      <Box className={classes.header}>
        <IconButton
          className={classes.headerIcon}
          onClick={(e) => {
            e.stopPropagation();
            setSidemenuOpen(true);
          }}
          size="large"
        >
          <FontAwesomeIcon icon="bars" size="xs" />
        </IconButton>
      </Box>

      <Box className={classes.container}>
        <IconButton
          className={classes.iconButton}
          aria-label={t('common:general')}
          onClick={(event) => handleSectionOpen('general', event)}
          size="large"
        >
          <FontAwesomeIcon icon="briefcase" />
        </IconButton>

        {showPaymentsMenu && (
        <IconButton
          className={classes.iconButton}
          aria-label={t('payments:payments')}
          onClick={(event) => handleSectionOpen('payments', event)}
          size="large"
        >
          <FontAwesomeIcon icon="money-bill-wave-alt" />
        </IconButton>
        )}

        {hasPermission('updateOrderTokens') && (
        <IconButton
          className={classes.iconButton}
          aria-label={t('closing:closing')}
          onClick={(event) => handleSectionOpen('closing', event)}
          size="large"
        >
          <FontAwesomeIcon icon="file-alt" />
        </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={section === 'general'}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleRoute('overview')}>
          {t('orders:overview')}
        </MenuItem>
        {orderType !== HUD_1 && hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('cash-to-close')}>
          {t('disclosure:cash-to-close')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('charges')}>
          {t('common:charges')}
        </MenuItem>
        )}
        {hasPermission('updateOrderConfiguration') && (
        <MenuItem onClick={() => handleRoute('dates-and-config')}>
          {t('orders:date-and-config')}
        </MenuItem>
        )}
        {hasPermission('createOrderFile') && (
        <MenuItem onClick={() => handleRoute('documents')}>
          {t('orders:documents')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('earnest-and-commissions')}>
          {t('earnestAndCommissions:earnest-and-commissions')}
        </MenuItem>
        )}
        {hasPermission('createOrderLoan') && (
        <MenuItem onClick={() => handleRoute('loans')}>
          {t('loan:loans')}
        </MenuItem>
        )}
        {hasPermission('createOrderMortgage') && (
        <MenuItem onClick={() => handleRoute('payoffs')}>
          {t('payoffs:payoffs')}
        </MenuItem>
        )}
        {hasPermission('createOrderNote') && (
        <MenuItem onClick={() => handleRoute('notes')}>
          {t('notes:notes')}
        </MenuItem>
        )}
        {hasPermission('sendNotification') && (
        <MenuItem onClick={() => handleRoute(NOTIFICATIONS)}>
          {t('messages:notifications')}
        </MenuItem>
        )}
        {hasPermission('createOrderAssociation') && (
        <MenuItem onClick={() => handleRoute('parties')}>
          {t('parties:parties')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('policy-info-and-rates')}>
          {t('policyInfoAndRates:policy-info-and-rates')}
        </MenuItem>
        )}
        {orderType !== HUD_1 && hasPermission('updateOrderProjected') && (
        <MenuItem onClick={() => handleRoute('projected-payments')}>
          {t('disclosure:projected-payments')}
        </MenuItem>
        )}
        {hasPermission('createOrderProperty') && (
        <MenuItem onClick={() => handleRoute('properties')}>
          {t('orders:properties')}
        </MenuItem>
        )}
        {hasPermission('createOrderProration') && (
        <MenuItem onClick={() => handleRoute('taxes-and-prorations')}>
          {t('taxesAndProrations:taxes-and-prorations')}
        </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={section === 'payments'}
        onClose={handleMenuClose}
      >
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('payments', 'accounting')}>
          {t('accounting:accounting')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute(ROUTE_DISBURSEMENTS_AND_RECEIPTS)}>
          {t('payments:disbursements-and-receipts')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('revenue')}>
          {t('revenue:title')}
        </MenuItem>
        )}
        {hasPermission('createOrderLedgerEntry') && (
        <MenuItem onClick={() => handleRoute('payments', 'settlement')}>
          {t('payments:settlement')}
        </MenuItem>
        )}
        {hasPermission('createTransferRequest') && (
        <MenuItem onClick={() => handleRoute('payments', 'wire-transfers')}>
          {t('payments:wire-tranfers')}
        </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={section === 'closing'}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleRoute('closing', 'schedule-closing')}>
          {t('closing:schedule-closing')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default CloseSidemenu;
